import { DatePicker, Dropdown, NumberInput, TextInput, TimePicker } from '@yembo/yemblocks-core';
import { getDateWithValidTime } from '@yembo/yemblocks-core/dist/TimePicker';
import dayjs from 'dayjs';
import { Dispatch, useRef, useState } from 'react';
import { EXPECTED_SURVEY_TIME_INTERVAL } from 'src/react/_routes/_util/expectedSurveyTimeUtils';
import { getDatePickerFormatting } from 'src/react/_routes/JobListing/_util/dateFormats';
import { getIsDateInPast } from 'src/react/_util/dates/getDate';
import { ContactJobState, CreateContactFormData, DefaultInsured } from 'src/react/Contact';
import { trimString } from 'src/util/format_data';

type Props = {
  job: ContactJobState;
  dispatchJobState: Dispatch<Partial<ContactJobState>>;
  dispatchInsured: (value: Partial<DefaultInsured>) => void;
  dispatchFormData: Dispatch<Partial<CreateContactFormData>>;
  isSelfSurvey: boolean;
  formData: CreateContactFormData;
};
export const JobSection = ({
  job,
  dispatchJobState,
  dispatchInsured,
  dispatchFormData,
  formData,
  isSelfSurvey,
}: Props) => {
  const { expectedSurveyDate, date } = job;
  const { company, origin, destination, insured, validation } = formData;
  const { businessUnits, isJobsEnabled } = company;
  const timePickerRef = useRef<HTMLInputElement>(null);
  const [dateSelected, setDateSelected] = useState<string | null>(null);

  const dispatchJob = (value: Partial<ContactJobState>) => dispatchJobState({ ...job, ...value });

  const handleJobDateChange = (date: Date | null) => {
    const updatedFields: {
      date: string | null;
      dateKnown: boolean;
      expectedSurveyDate?: string | null;
    } = {
      date: date && dayjs(date).startOf('day').format('YYYY-MM-DD'),
      dateKnown: Boolean(date),
    };

    if (expectedSurveyDate && date && expectedSurveyDate > date.toISOString()) {
      updatedFields.expectedSurveyDate = null;
    }

    dispatchJob({ ...updatedFields });
  };

  // calculate Expected Survey Time
  const currentTime = new Date();
  let expectedSurveyTime = expectedSurveyDate ? new Date(expectedSurveyDate) : null;
  if (expectedSurveyTime && currentTime > expectedSurveyTime) {
    // round to next hour, e.g. for 8:15 current time -> outputs 9:00
    expectedSurveyTime = dayjs(currentTime).add(1, 'hour').startOf('hour').toDate();
    dispatchJob({ expectedSurveyDate: expectedSurveyTime?.toISOString() });
  }

  return (
    <div className='section job-section'>
      <p className='bold header'>Job</p>
      <div className='column job-column'>
        <DatePicker
          label='Job Date'
          selected={date ? dayjs(new Date(date)).subtract(dayjs(date).utcOffset(), 'minutes').toDate() : null}
          onChange={handleJobDateChange}
          minDate={isJobsEnabled ? undefined : new Date()}
          {...getDatePickerFormatting({ company })}
        />

        {isSelfSurvey && (
          <div className='inline'>
            <DatePicker
              label='Expected Survey Date'
              selected={expectedSurveyDate ? new Date(expectedSurveyDate) : null}
              onChange={(date) => {
                const validDate = date && getDateWithValidTime(date, new Date(), EXPECTED_SURVEY_TIME_INTERVAL);

                // Default to noon, not midnight, when date changes
                validDate?.setHours(12, 0, 0, 0);

                dispatchJob({ expectedSurveyDate: validDate?.toISOString() });
                if (!validDate) setDateSelected(null);
              }}
              minDate={new Date()}
              {...getDatePickerFormatting({ company })}
              onCalendarClose={() => {
                if (!dateSelected && expectedSurveyDate) {
                  setDateSelected(expectedSurveyDate);
                  timePickerRef?.current?.click();
                }
              }}
            />
            <TimePicker
              label='Expected Survey Time'
              time={expectedSurveyTime}
              onChange={(date) => {
                dispatchJob({ expectedSurveyDate: date?.toISOString() });
                if (!date) setDateSelected(null);
              }}
              filterTime={(date) => !getIsDateInPast(date)}
              timeIntervals={EXPECTED_SURVEY_TIME_INTERVAL}
              clickableElementRef={timePickerRef}
            />
          </div>
        )}
        <div className='inline'>
          <NumberInput
            label='# of Bedrooms'
            name='bedrooms'
            min={0}
            max={99}
            defaultValue={origin.beds ?? ''}
            onChange={({ currentTarget: { valueAsNumber, validity } }) => {
              dispatchFormData({
                origin: { ...origin, beds: valueAsNumber },
                destination: { ...destination, beds: valueAsNumber },
                validation: {
                  ...validation,
                  isNumberOfBedsValid: validity.valid,
                },
              });
            }}
          />
          <TextInput
            label='Customer ID'
            name='customerId'
            value={insured.id ?? ''}
            onChange={(event) => dispatchInsured({ id: event.currentTarget.value })}
            onBlur={(event) => dispatchInsured({ id: trimString(event.target.value ?? '') })} // don't allow blank space for customer ID
          />
        </div>
        <Dropdown
          label='Business Unit'
          enableSearch
          options={businessUnits.map((businessUnit) => ({
            key: businessUnit,
            name: businessUnit,
            isSelected: businessUnit === job.businessUnit,
            onClick: () => dispatchJobState({ ...job, businessUnit }),
          }))}
          placeholder='Select'
          className='business-unit-add-contact'
        />
      </div>
    </div>
  );
};
